import { todaySaoPaulo } from "../../utils/format";
import InfoHelp from "../../components/help/InfoHelp";
import { metricHelp } from "../../components/help/helpText";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { saveBlobResponse } from "../../utils/download";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  ClipboardPlus,
  FileText,
  GraduationCap,
  History,
  Inbox,
  MapPin,
  Pencil,
  Repeat2,
  ShieldCheck,
} from "lucide-react";

const stateLabel = {
  vencida: "Vencida",
  vence_7d: "Até 7 dias",
  vence_30d: "Até 30 dias",
  vence_60d: "Até 60 dias",
  sem_prazo: "Sem prazo",
  ok: "Regular",
  futura: "Futura",
  aguardando_validacao: "Aguardando conferência",
};

const typeLabel = {
  action: "Plano de ação",
  evidence: "Evidência",
  task: "Pendência",
  visit: "Visita",
  fire: "Incêndio",
  automation: "Automação",
  follow_up: "Contato",
  training: "Treinamento",
  obligation: "Obrigação",
};

const proposalStatusLabel = {
  rascunho: "Rascunho",
  enviada: "Enviada",
  aceita: "Aceita",
  recusada: "Recusada",
};

function proposalCode(p) {
  if (p?.codigo_base && p?.revision_number != null) {
    return `${p.codigo_base}-R${String(p.revision_number).padStart(2, "0")}`;
  }
  return p?.codigo || (p?.numero != null ? String(p.numero) : "Proposta");
}

function proposalBadgeVariant(status) {
  if (status === "recusada") return "destructive";
  if (status === "aceita") return "default";
  if (status === "rascunho") return "secondary";
  return "outline";
}

function Metric({ label, value, icon: Icon, danger = false, hint }) {
  return (
    <Card
      className={`min-w-0 ${danger ? "border-destructive/35" : "border-border/75"}`}
    >
      <CardContent className="min-w-0 p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium leading-snug text-muted-foreground">
              {label}<InfoHelp label={label}>{metricHelp[label] || hint || "Indicador calculado para os registros e filtros desta tela."}</InfoHelp>
            </div>
            <div
              className={`mt-2 font-mono text-2xl font-semibold leading-none tabular-nums ${danger ? "text-destructive" : ""}`}
            >
              {value}
            </div>
            {hint && (
              <div className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {hint}
              </div>
            )}
          </div>
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Icon className="size-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function obligationState(value) {
  if (!value) return "sem_prazo";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  const delta = Math.round((due - today) / 86400000);
  if (delta < 0) return "vencida";
  if (delta <= 7) return "vence_7d";
  if (delta <= 30) return "vence_30d";
  if (delta <= 60) return "vence_60d";
  return "futura";
}

function Empty({ children }) {
  return (
    <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

export default function Client360() {
  const { can, readOnly } = useAuth();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [obligations, setObligations] = useState([]);
  const [queue, setQueue] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [partialError, setPartialError] = useState(false);
  const [completingObligation, setCompletingObligation] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    setPartialError(false);
    try {
      const [snapshot, obligationsResult, queueResult] = await Promise.allSettled([
        api.get(`/operations/client/${id}/360`),
        api.get(
          `/efficiency/obligations?client_id=${encodeURIComponent(id)}&status=ativa&limit=500`,
        ),
        api.get(
          `/efficiency/work-queue?client_id=${encodeURIComponent(id)}&limit=300`,
        ),
      ]);
      if (snapshot.status === "rejected") throw snapshot.reason;
      setData(snapshot.value.data);
      setObligations(obligationsResult.status === "fulfilled" ? obligationsResult.value.data : []);
      setQueue(queueResult.status === "fulfilled" ? queueResult.value.data : { items: [], total: 0, counts: {} });
      setPartialError(obligationsResult.status === "rejected" || queueResult.status === "rejected");
    } catch (e) {
      setLoadError("Não foi possível carregar a central do cliente. Tente novamente ou abra o cadastro.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line

  const timeline = useMemo(() => {
    if (!data) return [];
    const rows = [];
    data.proposals?.forEach((p) =>
      rows.push({
        date: p.updated_at || p.created_at || p.data,
        type: "Proposta",
        title: proposalCode(p),
        sub: p.status,
      }),
    );
    data.documents?.forEach((d) =>
      rows.push({
        date: d.created_at || d.data_emissao,
        type: "Documento",
        title: d.tipo || "Documento",
        sub: d.status || "concluído",
      }),
    );
    data.visits?.forEach((v) =>
      rows.push({
        date: v.scheduled_at || v.created_at,
        type: "Visita",
        title: v.titulo,
        sub: v.status,
      }),
    );
    data.trainings?.forEach((t) =>
      rows.push({
        date: t.emitted_at || t.data_base,
        type: "Treinamento",
        title: t.treinamento_nome,
        sub: t.preparado ? "Documentos preparados; datas a preencher" : t.emitir_certificado === false ? "Registro de presença" : `vence ${t.data_vencimento || "não informado"}`,
      }),
    );
    obligations.forEach((o) =>
      rows.push({
        date: o.updated_at || o.created_at,
        type: "Obrigação",
        title: o.titulo,
        sub: `próximo vencimento ${o.next_due_at || "não definido"}`,
      }),
    );
    return rows
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
      .slice(0, 100);
  }, [data, obligations]);

  const attention = useMemo(() => {
    if (!data) return [];
    const rows = [];
    queue.items?.forEach((item) =>
      rows.push({
        key: `${item.type}-${item.id}`,
        type: item.type,
        title: item.title,
        detail: item.subtitle,
        state: item.deadline_state,
        date: item.due_at,
        priority: item.priority,
        href: item.href,
      }),
    );
    obligations.forEach((item) => {
      const state = obligationState(item.next_due_at);
      if (
        ![
          "vencida",
          "vence_7d",
          "vence_30d",
          "vence_60d",
          "sem_prazo",
        ].includes(state)
      )
        return;
      rows.push({
        key: `obligation-${item.id}`,
        type: "obligation",
        title: item.titulo,
        detail: item.norma || item.categoria,
        state,
        date: item.next_due_at,
        priority: item.prioridade,
        href: "/operacoes/obrigacoes",
      });
    });
    const weight = {
      vencida: 0,
      aguardando_validacao: 1,
      vence_7d: 2,
      vence_30d: 3,
      vence_60d: 4,
      sem_prazo: 5,
      futura: 6,
      ok: 7,
    };
    return rows
      .filter(
        (row, index, all) => all.findIndex((x) => x.key === row.key) === index,
      )
      .sort(
        (a, b) =>
          (weight[a.state] ?? 9) - (weight[b.state] ?? 9) ||
          String(a.date || "9999").localeCompare(String(b.date || "9999")),
      )
      .slice(0, 8);
  }, [data, queue.items, obligations]);

  const obligationMetrics = useMemo(
    () => ({
      overdue: obligations.filter(
        (o) => obligationState(o.next_due_at) === "vencida",
      ).length,
      due60: obligations.filter((o) =>
        ["vencida", "vence_7d", "vence_30d", "vence_60d"].includes(
          obligationState(o.next_due_at),
        ),
      ).length,
    }),
    [obligations],
  );

  const completeObligation = async (item) => {
    setCompletingObligation(item.id);
    try {
      const r = await api.post(`/efficiency/obligations/${item.id}/complete`, {
        completed_at: null,
        evidence_note: "Ciclo concluído a partir do cliente",
      });
      const next = r.data?.obligation?.next_due_at;
      toast.success(
        next ? `Concluído. Próxima renovação: ${next}` : "Obrigação concluída",
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao concluir obrigação");
    } finally {
      setCompletingObligation("");
    }
  };

  if (loading)
    return (
      <>
        <Header title="Cliente" />
        <div className="page-container">
          <div className="h-48 animate-pulse rounded-2xl border bg-card/50" />
        </div>
      </>
    );
  if (!data || loadError) return <><Header title="Central do cliente" /><div className="page-container space-y-4" role="alert"><p>{loadError || "Cliente indisponível."}</p><Button onClick={load}>Tentar novamente</Button> <Link to={`/clientes/${id}`}><Button variant="outline">Abrir cadastro</Button></Link> <Link to="/clientes"><Button variant="outline">Voltar aos clientes</Button></Link></div></>;
  const c = data.client;
  const overdueActions = data.metrics.actions_overdue || 0;
  const reviewPending = data.metrics.evidence_pending || 0;
  const totalAttention = attention.length;
  const downloadDocument = async (document) => {
    try { const response = await api.get(`/documents/${document.id}/download`, { responseType: "blob" }); saveBlobResponse(response, document.tipo || "Documento"); }
    catch { toast.error("Não foi possível baixar o documento deste cliente"); }
  };
  const newProposalUrl = `/propostas/nova?client_id=${encodeURIComponent(id)}`;

  return (
    <>
      <Header
        title={c.razao_social}
        subtitle="Tudo deste cliente em um só lugar"
        actions={
          <div className="flex flex-wrap gap-2">
            {data.permissions?.proposals !== false &&
              !data.permissions?.read_only && (
                <Link to={newProposalUrl}>
                  <Button data-testid="client360-new-proposal">
                    <ClipboardPlus className="size-4" /> Nova proposta
                  </Button>
                </Link>
              )}
            <Link to="/clientes">
              <Button variant="outline">
                <ArrowLeft className="size-4" /> Clientes
              </Button>
            </Link>
            <Link to={`/clientes/${id}`}>
              <Button variant="outline">
                <Pencil className="size-4" /> Editar cadastro
              </Button>
            </Link>
          </div>
        }
      />
      <div className="page-container space-y-6" data-testid="client-360-page">
        {partialError && <div role="alert" className="rounded-xl border border-amber-500/40 p-3">Parte das pendências ou renovações não pôde ser consultada. Os dados abaixo podem estar incompletos. <Button variant="outline" onClick={load}>Tentar novamente</Button></div>}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric
            icon={AlertTriangle}
            label="Ações atrasadas"
            value={overdueActions}
            hint="Planos de ação com prazo vencido"
            danger={overdueActions > 0}
          />
          <Metric
            icon={ShieldCheck}
            label="Para conferir"
            value={reviewPending}
            hint="Evidências aguardando revisão"
            danger={reviewPending > 0}
          />
          <Metric
            icon={Repeat2}
            label="Renovações até 60 dias"
            value={obligationMetrics.due60}
            hint={`${obligationMetrics.overdue} já vencida(s)`}
            danger={obligationMetrics.overdue > 0}
          />
          <Metric
            icon={Inbox}
            label="Pendências"
            value={queue.total || 0}
            hint="Itens deste cliente na fila de trabalho"
          />
        </div>

        <Card>
          <CardContent className="p-5">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <div>
                <div className="label-tech">CNPJ</div>
                <div className="mt-1 text-sm font-medium">{c.cnpj || "—"}</div>
              </div>
              <div>
                <div className="label-tech">Município</div>
                <div className="mt-1 text-sm font-medium">
                  {[c.municipio, c.uf].filter(Boolean).join(" / ") || "—"}
                </div>
              </div>
              <div>
                <div className="label-tech">CNAE</div>
                <div className="mt-1 text-sm font-medium">
                  {c.cnae || c.cnae_principal || "—"}
                </div>
              </div>
              <div>
                <div className="label-tech">Atividade</div>
                <div className="mt-1 text-sm font-medium">
                  {c.atividade_principal || "—"}
                </div>
              </div>
              <div>
                <div className="label-tech">Porte operacional</div>
                <div className="mt-1 text-sm font-medium">
                  GR {c.grau_risco || "—"} · {c.num_funcionarios || 0}{" "}
                  funcionário(s)
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={
            totalAttention ? "border-amber-500/25" : "border-primary/20"
          }
        >
          <CardContent className="p-5 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-xs font-medium text-muted-foreground">
                  Prioridades do cliente
                </div>
                <h2 className="mt-1 text-lg font-semibold">
                  {totalAttention
                    ? "O que precisa de atenção"
                    : "Nenhuma pendência importante agora"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Somente itens registrados no CEO. O sistema não cria obrigação
                  ou risco por inferência do CNAE.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/operacoes/fila">
                  <Button variant="outline" size="sm">
                    <Inbox className="size-4" /> Ver todas as pendências
                  </Button>
                </Link>
                <Link to="/operacoes/obrigacoes">
                  <Button variant="outline" size="sm">
                    <Repeat2 className="size-4" /> Ver renovações
                  </Button>
                </Link>
              </div>
            </div>
            {attention.length > 0 ? (
              <div className="mt-5 space-y-2">
                {attention.map((item) => (
                  <div
                    key={item.key}
                    className={`flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${item.state === "vencida" ? "border-destructive/35" : "border-border/70"}`}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={
                            item.state === "vencida" ? "destructive" : "outline"
                          }
                        >
                          {stateLabel[item.state] || item.state}
                        </Badge>
                        <Badge variant="secondary">
                          {typeLabel[item.type] || item.type}
                        </Badge>
                      </div>
                      <div className="mt-2 font-medium">{item.title}</div>
                      {item.detail && (
                        <div className="mt-1 text-sm text-muted-foreground">
                          {item.detail}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-muted-foreground">
                        Prazo:{" "}
                        {String(item.date || "—")
                          .slice(0, 16)
                          .replace("T", " ")}
                      </div>
                    </div>
                    {item.href && (
                      <Link to={item.href}>
                        <Button variant="ghost" size="sm">
                          Abrir <ArrowRight className="size-3.5" />
                        </Button>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-5">
                <Empty>
                  <CheckCircle2 className="mx-auto mb-2 size-5 text-primary" />
                  Nenhuma pendência registrada para este cliente.
                </Empty>
              </div>
            )}
          </CardContent>
        </Card>

        {!!data.fire_units?.length && (
          <Card>
            <CardContent className="p-5">
              <h2 className="font-semibold">Proteção contra incêndio</h2>
              <div className="mt-3 flex flex-wrap gap-3">
                {data.fire_units.map((unit) => (
                  <Link
                    key={unit.id}
                    to={`/incendio?unit_id=${encodeURIComponent(unit.id)}`}
                  >
                    <Button variant="outline">
                      <ShieldCheck className="size-4" />
                      {unit.nome}
                    </Button>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="acompanhamento">
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="acompanhamento">
              <ClipboardCheck className="mr-2 size-4" />
              Acompanhamento
            </TabsTrigger>
            <TabsTrigger value="propostas">
              <ClipboardPlus className="mr-2 size-4" />
              Propostas
            </TabsTrigger>
            <TabsTrigger value="documentos">
              <FileText className="mr-2 size-4" />
              Documentos
            </TabsTrigger>
            <TabsTrigger value="treinamentos">
              <GraduationCap className="mr-2 size-4" />
              Treinamentos
            </TabsTrigger>
            <TabsTrigger value="historico">
              <History className="mr-2 size-4" />
              Histórico
            </TabsTrigger>
          </TabsList>

          <TabsContent value="acompanhamento" className="mt-5 space-y-5">
          <div className="mt-3 flex flex-wrap gap-2">
            {can("can_access_operations") && !readOnly && <><Link to={`/operacoes?tab=importar&client_id=${encodeURIComponent(id)}`}><Button variant="outline" size="sm">Importar plano do PGR</Button></Link><Link to={`/operacoes?tab=visitas&client_id=${encodeURIComponent(id)}`}><Button variant="outline" size="sm">Agendar visita para este cliente</Button></Link></>}
          </div>

            <section>
              <div className="mb-3">
                <div className="label-tech">Planos de ação</div>
                <h3 className="mt-1 font-semibold">Ações deste cliente</h3>
              </div>
              <div className="space-y-3">
                {data.actions?.map((a) => (
                  <Card
                    key={a.id}
                    className={
                      a.deadline_state === "vencida"
                        ? "border-destructive/35"
                        : ""
                    }
                  >
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={
                            a.deadline_state === "vencida"
                              ? "destructive"
                              : "outline"
                          }
                        >
                          {stateLabel[a.deadline_state] || a.deadline_state}
                        </Badge>
                        <Badge variant="secondary">
                          {a.status?.replaceAll("_", " ")}
                        </Badge>
                        {a.norma && <Badge variant="outline">{a.norma}</Badge>}
                      </div>
                      <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">
                        {a.texto_original}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                        <span>Prazo: {a.next_due_at || a.prazo || "—"}</span>
                        <span>Responsável: {a.responsavel || "—"}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {!data.actions?.length && (
                  <Empty>Nenhuma ação registrada.</Empty>
                )}
              </div>
            </section>

            <section>
              <div className="mb-3">
                <div className="label-tech">Renovações</div>
                <h3 className="mt-1 font-semibold">Obrigações recorrentes</h3>
              </div>
              <div className="space-y-3">
                {obligations.map((o) => {
                  const state = obligationState(o.next_due_at);
                  return (
                    <Card
                      key={o.id}
                      className={
                        state === "vencida" ? "border-destructive/35" : ""
                      }
                    >
                      <CardContent className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge
                              variant={
                                state === "vencida" ? "destructive" : "outline"
                              }
                            >
                              {stateLabel[state] || state}
                            </Badge>
                            <Badge variant="secondary">{o.categoria}</Badge>
                            {o.norma && (
                              <Badge variant="outline">{o.norma}</Badge>
                            )}
                          </div>
                          <div className="mt-2 font-medium">{o.titulo}</div>
                          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                            <span>Próximo: {o.next_due_at || "—"}</span>
                            <span>Recorrência: {o.recorrencia}</span>
                            <span>Responsável: {o.responsavel || "—"}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          disabled={completingObligation === o.id}
                          onClick={() => completeObligation(o)}
                        >
                          <CheckCircle2 className="size-4" /> Concluir ciclo
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
                {!obligations.length && (
                  <Empty>Nenhuma renovação ou obrigação ativa.</Empty>
                )}
              </div>
            </section>

            <section>
              <div className="mb-3">
                <div className="label-tech">Equipe</div>
                <h3 className="mt-1 font-semibold">Outras pendências</h3>
              </div>
              <div className="space-y-3">
                {data.tasks?.map((t) => (
                  <Card key={t.id}>
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{t.status}</Badge>
                        <Badge variant="secondary">{t.prioridade}</Badge>
                      </div>
                      <div className="mt-2 font-medium">{t.titulo}</div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {t.descricao}
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        Prazo: {t.due_at || "—"} · Responsável:{" "}
                        {t.responsavel || "—"}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {!data.tasks?.length && (
                  <Empty>Nenhuma pendência interna registrada.</Empty>
                )}
              </div>
            </section>
          </TabsContent>

          <TabsContent
            value="propostas"
            className="mt-5 space-y-4"
            data-testid="client360-proposals-tab"
          >
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="label-tech">Comercial</div>
                <h3 className="mt-1 font-semibold">Propostas deste cliente</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Crie a proposta a partir daqui para abrir o wizard com este
                  cliente já selecionado.
                </p>
              </div>
              {data.permissions?.proposals !== false &&
                !data.permissions?.read_only && (
                  <Link to={newProposalUrl}>
                    <Button>
                      <ClipboardPlus className="size-4" /> Criar proposta
                    </Button>
                  </Link>
                )}
            </div>
            <div className="space-y-3">
              {(data.proposals || []).map((p) => (
                <Card
                  key={
                    p.id ||
                    `${proposalCode(p)}-${p.updated_at || p.created_at || p.data || ""}`
                  }
                >
                  <CardContent className="flex flex-col gap-4 p-4 sm:p-5 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-semibold">{proposalCode(p)}</div>
                        <Badge variant={proposalBadgeVariant(p.status)}>
                          {proposalStatusLabel[p.status] ||
                            p.status ||
                            "Sem status"}
                        </Badge>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Data:{" "}
                        {String(p.data || p.created_at || "—").slice(0, 10)}
                        {p.updated_at
                          ? ` · Atualizada: ${String(p.updated_at).slice(0, 16).replace("T", " ")}`
                          : ""}
                      </div>
                    </div>
                    {p.id && (
                      <Link to={`/propostas/${p.id}`}>
                        <Button variant="outline" size="sm">
                          Abrir proposta <ArrowRight className="size-3.5" />
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              ))}
              {!data.proposals?.length && (
                <Empty>
                  <ClipboardPlus className="mx-auto mb-2 size-5 text-primary" />
                  Nenhuma proposta registrada para este cliente. Use “Criar
                  proposta” para iniciar com o cliente pré-selecionado.
                </Empty>
              )}
            </div>
          </TabsContent>

          <TabsContent value="documentos" className="mt-5">
            <div className="grid gap-3 lg:grid-cols-2">
              {data.documents?.map((d) => (
                <Card key={d.id}>
                  <CardContent className="p-4">
                    <div className="font-medium">{d.tipo}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Emissão: {d.data_emissao || "—"} ·{" "}
                      {d.status || "concluído"}
                    </div>
                    {d.status !== "processing" && d.status !== "failed" && <Button variant="outline" size="sm" className="mt-3" onClick={() => downloadDocument(d)}>Baixar documento</Button>}
                  </CardContent>
                </Card>
              ))}
              {!data.documents?.length && (
                <Empty>Nenhum documento gerado no CEO.</Empty>
              )}
            </div>
          </TabsContent>

          <TabsContent value="treinamentos" className="mt-5">
            {can("can_access_training") && !readOnly && <Link to={`/treinamentos?tab=emissao&client_id=${encodeURIComponent(id)}`}><Button className="mb-4">Preparar ou emitir treinamento para este cliente</Button></Link>}
            <div className="space-y-3">
              {data.trainings?.map((t) => (
                <Card key={t.id}>
                  <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="font-medium">{t.treinamento_nome}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {t.participantes_count} participante(s) · emissão{" "}
                        {t.preparado ? "documentos preparados" : t.data_base || "registro de presença"}
                      </div>
                    </div>
                    <Badge
                      variant={
                        String(t.data_vencimento) <
                        todaySaoPaulo()
                          ? "destructive"
                          : "outline"
                      }
                    >
                      {t.preparado ? "A preencher à mão" : t.emitir_certificado === false ? "Somente presença" : `Vence ${t.data_vencimento || "não informado"}`}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
              {!data.trainings?.length && (
                <Empty>Nenhum treinamento vinculado a este cliente.</Empty>
              )}
            </div>
          </TabsContent>

          <TabsContent value="historico" className="mt-5 space-y-6">
            <section>
              <div className="mb-3">
                <div className="label-tech">Visitas</div>
                <h3 className="mt-1 font-semibold">Visitas técnicas</h3>
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {data.visits?.map((v) => (
                  <Card key={v.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">{v.titulo}</div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {v.scheduled_at || "Sem data"} · {v.status}
                          </div>
                        </div>
                        <Link to={`/operacoes/visitas/${v.id}`}>
                          <Button variant="outline" size="sm">
                            Abrir
                          </Button>
                        </Link>
                      </div>
                      <p className="mt-3 text-sm text-muted-foreground">
                        {v.objetivo}
                      </p>
                    </CardContent>
                  </Card>
                ))}
                {!data.visits?.length && (
                  <Empty>Nenhuma visita registrada.</Empty>
                )}
              </div>
            </section>
            <section>
              <div className="mb-3">
                <div className="label-tech">Linha do tempo</div>
                <h3 className="mt-1 font-semibold">Histórico do cliente</h3>
              </div>
              <Card>
                <CardContent className="p-5">
                  <div className="space-y-1">
                    {timeline.map((item, i) => (
                      <div
                        key={`${item.type}-${i}`}
                        className="flex gap-4 border-b border-border/60 py-3 last:border-0"
                      >
                        <div className="w-32 shrink-0 font-mono text-xs text-muted-foreground">
                          {String(item.date || "")
                            .slice(0, 16)
                            .replace("T", " ")}
                        </div>
                        <div>
                          <div className="text-sm font-medium">
                            {item.title}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {item.type} · {item.sub}
                          </div>
                        </div>
                      </div>
                    ))}
                    {!timeline.length && (
                      <p className="text-sm text-muted-foreground">
                        Sem eventos para exibir.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </section>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
